import { Identify } from "@amplitude/analytics-browser";
import {
  investorExchangeAmplitude,
  isInvestorExchangeInitialized
} from "./amplitude-config";

// Types
interface BaseEventProperties {
  timestamp?: string;
  user_id?: string;
}

interface EventProperties extends BaseEventProperties {
  property_id: string;
  property_address?: string;
  property_sfid: string;
  investor_id?: string;
  investor_sfid?: string;
  search_term?: string;
  bid_status?: string;
  interest_score?: number;
  contact?: {
    id: string;
    name: string;
    sfid: string;
    email: string;
    mobile: string;
  };
  filter_values?: {
    radius: string | number;
    view: string;
    acctType: string;
    order: string;
    checkoutStatus: string;
    shouldIgnoreRadius: any;
  };
  bid_details?: {
    amount: string;
    contactId: string;
    pofFileName: string | null;
    rpaFileName: string | null;
    purchaseType: string;
    buyerBeenInside: string;
    referralFee?: string;
    buysInName: string;
    lender: string;
    buyerVesting: string;
    // buyerVesting: {
    //   entitySFID: string;
    //   entityName: string;
    //   createdDate: string;
    // };
  };
}

// Helper function to get user data from localStorage
const getUserData = () => {
  const userObj = localStorage.getItem("user-detail") || "";
  const userDetail = userObj && JSON.parse(userObj);
  return { user_id: userDetail?.sfid };
};

// Helper function to track events with error handling
const trackEvent = (eventName: string, properties: Record<string, any>) => {
  if (!isInvestorExchangeInitialized) {
    console.error("Amplitude not initialized");
    return;
  }
  try {
    const userData = getUserData();
    const eventProperties = {
      ...userData,
      ...properties,
      timestamp: new Date().toISOString()
    };

    investorExchangeAmplitude.track(eventName, eventProperties);
  } catch (error) {
    console.error(`Failed to track event ${eventName}:`, error);
  }
};

export const LoginWithSF = ({
  user_id,
  login_method
}: {
  login_method: string;
  user_id: string;
}) => {
  if (!isInvestorExchangeInitialized) return;
  try {
    investorExchangeAmplitude.setUserId(user_id);
    const identify = new Identify().set("user_id", user_id);
    investorExchangeAmplitude.identify(identify);
    trackEvent("User logs in via Salesforce", {
      user_id,
      login_method
    });
  } catch (error) {
    console.error("Failed to track login event:", error);
  }
};

export const NavigateToExchange = (properties: EventProperties) => {
  trackEvent("User accesses the Investor Exchange for a property", properties);
};

export const InvestorCheckedOut = (properties: EventProperties) => {
  trackEvent("Investor is checked out by user", properties);
};

export const CallButtonClicked = (properties: EventProperties) => {
  trackEvent("User clicks call button", properties);
};

export const EmailButtonClicked = (properties: EventProperties) => {
  trackEvent("User clicks email button", properties);
};

export const TextButtonClicked = (properties: EventProperties) => {
  trackEvent("User clicks text message button", properties);
};

export const InterestScoreClicked = (properties: EventProperties) => {
  trackEvent("User clicks the interest score", properties);
};

export const PurchaseHistoryViewed = (properties: EventProperties) => {
  trackEvent("User expands purchase history", properties);
};

export const InvestorSearchExecuted = (properties: EventProperties) => {
  trackEvent("User runs a search", properties);
};

export const FiltersApplied = (properties: EventProperties) => {
  trackEvent("User applies filters", properties);
};

export const InvestorBidInitiated = (properties: EventProperties) => {
  trackEvent("User initiates a bid", properties);
};

export const InvestorBidSubmitted = (properties: EventProperties) => {
  trackEvent("User submits a bid form", properties);
};

export const InvestorDashboardOpened = (properties: EventProperties) => {
  trackEvent("User opens investor dashboard", properties);
};

export const ToggleMyAccountsView = () => {
  trackEvent("User switches to 'My Accounts' tab", {});
};

export const ToggleMyCheckoutsView = () => {
  trackEvent("User switches to 'My Checkouts' tab", {});
};

export const NavigateBackToExchange = (properties: EventProperties) => {
  trackEvent("Click to return to exchange from dashboard", properties);
};

export const InvestorBidConversion = (properties: EventProperties) => {
  trackEvent("Investor who was checked out ends up placing a bid", properties);
};

export const InvestorEventsClicked = (properties: EventProperties) => {
  trackEvent("User clicks to view investor portal events", properties);
};

export const InvestorViewed = (properties: EventProperties) => {
  trackEvent("User views an investor card", properties);
};
